import { useCallback, useEffect, useState } from "react";
import DataTable from "../utils/Datatable";
import Modal from "../utils/Modal";
import apiClient from "../api/apiClient";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export default function AdminList({ type }) {
  const [data, setData] = useState([]);
  const [editRow, setEditRow] = useState(null);

  // 🔥 DRIVER DETAILS STATE
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [successMsg, setSuccessMsg] = useState("");
  const token = localStorage.getItem("token");

  const fetchUrl = type === "drivers" ? "/admin/drivers" : "/admin/users";
  const title = type === "drivers" ? "Drivers" : "Users";

  const showMessage = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 2000);
  };

  const fetchData = useCallback(async () => {
    const res = await apiClient.get(fetchUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setData(res.data);
  }, [fetchUrl, token]);

  // ---------------- FETCH LIST ----------------
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadImage = async (imageName) => {
    if (!imageName) return null;

    try {
      const res = await apiClient.get(`/images/${imageName}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      return URL.createObjectURL(res.data);
    } catch {
      return null;
    }
  };

  const downloadDriverData = async () => {
    if (!details) return;

    const zip = new JSZip();

    // Driver Details Text
    const driverInfo = `
Driver Details

Driver ID : ${details.driverId}
Name      : ${details.name}
Mobile    : ${details.mobile}
Email     : ${details.email || "-"}

Status    : ${details.isBlocked ? "Blocked" : "Active"}

Vehicle Details
---------------
Vehicle Number : ${details.vehicle?.vehicleNumber || "-"}
Vehicle Type   : ${details.vehicle?.vehicleType || "-"}
Vehicle SubType: ${details.vehicle?.vehicleSubType || "-"}
City           : ${details.vehicle?.city || "-"}

License Details
---------------
Driver Name       : ${details.license?.driverName || "-"}
Driver Phone      : ${details.license?.driverPhone || "-"}
Drive This Vehicle: ${details.license?.driveThisVehicle ? "Yes" : "No"
      }
`;

    zip.file("Driver_Details.txt", driverInfo);

    const addImage = async (url, fileName) => {
      if (!url) return;

      const response = await fetch(url);
      const blob = await response.blob();

      zip.file(fileName, blob);
    };

    await addImage(details.photos?.photo1, "Driver_Photo_1.jpg");
    await addImage(details.photos?.photo2, "Driver_Photo_2.jpg");
    await addImage(details.photos?.photo3, "Driver_Photo_3.jpg");
    await addImage(details.vehicle?.vehicleImage, "Vehicle_Image.jpg");
    await addImage(details.license?.licenseImage, "License_Image.jpg");

    const content = await zip.generateAsync({ type: "blob" });

    saveAs(content, `Driver_${details.driverId}.zip`);
  };

  // ---------------- DELETE ----------------
  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${title.slice(0, -1)}?`)) return;

    await apiClient.delete(`/admin/users/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setData((prev) => prev.filter((i) => i.id !== id));
    showMessage(`${title.slice(0, -1)} deleted`);
  };

  // ---------------- BLOCK ----------------
  const toggleBlock = async (id, block) => {
    await apiClient.patch(
      `/admin/users/${id}/block?block=${block}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setData((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isBlocked: block } : item
      )
    );
  };

  // ---------------- SAVE EDIT ----------------
  const saveEdit = async () => {
    await apiClient.put(`/admin/users/${editRow.id}`, form, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setData((prev) =>
      prev.map((i) => (i.id === editRow.id ? { ...i, ...form } : i))
    );

    setEditRow(null);
    showMessage("Updated successfully");
  };

  // ---------------- DRIVER DETAILS API ----------------
  const fetchDriverDetails = async (driverId) => {
    try {
      setLoadingDetails(true);
      setDetails(null);

      const res = await apiClient.get(`/admin/${driverId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = res.data;

      // 🔥 IMPORTANT: wait for ALL images
      const photo1 = await loadImage(d.photos?.photo1);
      const photo2 = await loadImage(d.photos?.photo2);
      const photo3 = await loadImage(d.photos?.photo3);
      const vehicleImage = await loadImage(d.vehicle?.vehicleImage);
      const licenseImage = await loadImage(d.license?.licenseImage);

      // 🔥 REPLACE IMAGE NAMES WITH BLOB URLs
      setDetails({
        ...d,
        photos: {
          photo1,
          photo2,
          photo3,
        },
        vehicle: {
          ...d.vehicle,
          vehicleImage,
        },
        license: {
          ...d.license,
          licenseImage,
        },
      });
    } catch {
      setDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <>
      <h2 className="page-title">{title}</h2>
      {successMsg && <div className="success-msg">{successMsg}</div>}

      <DataTable
        data={data}
        isDriver={type === "drivers"}
        onEdit={(row) => {
          setEditRow(row);
          setForm({
            firstName: row.firstName || "",
            lastName: row.lastName || "",
            email: row.email || "",
          });
        }}
        onDelete={handleDelete}
        onToggleBlock={toggleBlock}
        onView={(row) => fetchDriverDetails(row.id)}
      />

      {/* ---------- EDIT MODAL ---------- */}
      {editRow && (
        <Modal
          title={`Edit ${title.slice(0, -1)}`}
          onClose={() => setEditRow(null)}
        >
          <input value={editRow.mobile} disabled />
          <input
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <input
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
          <input
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <button className="btn-primary" onClick={saveEdit}>
            Save
          </button>
        </Modal>
      )}

      {/* ---------- DRIVER DETAILS MODAL ---------- */}
      {(loadingDetails || details) && type === "drivers" && (
        <Modal
          title="Driver Full Details"
          onClose={() => {
            setDetails(null);
            setLoadingDetails(false);
          }}
        >
          {loadingDetails ? (
            <div className="details-loader">
              <div className="spinner"></div>
              <p>Loading driver details...</p>
            </div>
          ) : (
            <div className="driver-details">
              {/* BASIC INFO */}
              <div className="details-section">
                <h3>Basic Information</h3>
                <div className="details-grid">
                  <p>
                    <b>ID:</b> {details.driverId}
                  </p>
                  <p>
                    <b>Name:</b> {details.name}
                  </p>
                  <p>
                    <b>Mobile:</b> {details.mobile}
                  </p>
                  <p>
                    <b>Email:</b> {details.email || "-"}
                  </p>
                  <p>
                    <b>Status:</b>
                    <span
                      className={`status ${details.isBlocked ? "blocked" : "active"
                        }`}
                    >
                      {details.isBlocked ? "Blocked" : "Active"}
                    </span>
                  </p>
                </div>
              </div>

              {/* PHOTOS */}
              <div className="details-section">
                <h3>Driver Photos</h3>
                <div className="image-grid">
                  {details.photos?.photo1 && (
                    <img
                      src={details.photos.photo1}
                      alt="Driver"
                    />
                  )}
                  {details.photos?.photo2 && (
                    <img
                      src={details.photos.photo2}
                      alt="Driver"
                    />
                  )}
                  {details.photos?.photo3 && (
                    <img
                      src={details.photos.photo3}
                      alt="Driver"
                    />
                  )}
                </div>
              </div>

              {/* VEHICLE */}
              <div className="details-section">
                <h3>Vehicle Details</h3>
                <div className="details-grid">
                  <p>
                    <b>Number:</b> {details.vehicle.vehicleNumber}
                  </p>
                  <p>
                    <b>Type:</b> {details.vehicle.vehicleType}
                  </p>
                  <p>
                    <b>Sub Type:</b> {details.vehicle.vehicleSubType}
                  </p>
                  <p>
                    <b>City:</b> {details.vehicle.city}
                  </p>
                </div>

                {details.vehicle.vehicleImage && (
                  <img
                    className="single-image"
                    src={details.vehicle.vehicleImage}
                    alt="Vehicle"
                  />
                )}
              </div>

              {/* LICENSE */}
              <div className="details-section">
                <h3>License Details</h3>
                <div className="details-grid">
                  <p>
                    <b>Name:</b> {details.license.driverName}
                  </p>
                  <p>
                    <b>Phone:</b> {details.license.driverPhone}
                  </p>
                  <p>
                    <b>Drive This Vehicle:</b>{" "}
                    {details.license.driveThisVehicle ? "Yes" : "No"}
                  </p>
                </div>

                {details.license.licenseImage && (
                  <img
                    className="single-image"
                    src={details.license.licenseImage}
                    alt="License"
                  />
                )}
              </div>

              <button
                className="btn-primary"
                onClick={downloadDriverData}
              >
                Download Driver Details
              </button>

              <button className="btn-primary" onClick={() => setDetails(null)}>
                Close
              </button>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
